import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  DefaultZIndexes,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from 'src/components/ui/chart';
import { buildChartConfig } from 'src/features/forecast/configs/chartConfig';
import { linesConfig } from 'src/features/forecast/configs/linesConfig';
import { yAxesConfig } from 'src/features/forecast/configs/yAxesConfig';
import { useForecastChart } from 'src/features/forecast/hooks/useForecastChart';
import { cn } from 'src/lib/utils';
import type { ForecastDataPoint } from 'src/types/domain/ForecastDataPoint';

type ForecastTranslation = TFunction<'forecast'>;

type ForecastChartProps = {
  data: ForecastDataPoint[];
  className?: string;
  isNovelty?: boolean;
};

type ForecastAxesProps = {
  t: ForecastTranslation;
};

type ForecastReferenceLineProps = {
  currentMonthId?: string;
  t: ForecastTranslation;
};

type ForecastTooltipProps = {
  t: ForecastTranslation;
};

function ForecastAxes({ t }: ForecastAxesProps) {
  return (
    <>
      <XAxis
        angle={-45}
        axisLine={false}
        dataKey='month_id'
        height={100}
        interval={0}
        minTickGap={10}
        scale='point'
        textAnchor='end'
        tick={{ fontSize: 10, fill: 'var(--chart-text)' }}
        tickLine={false}
        type='category'
        xAxisId={0}
      />
      {yAxesConfig.map((yAxisConfig) => (
        <YAxis
          key={yAxisConfig.yAxisId}
          {...yAxisConfig}
          label={{
            ...yAxisConfig.label,
            value: t(yAxisConfig.label.value),
          }}
        />
      ))}
    </>
  );
}

function ForecastCurrentMonthReference({
  currentMonthId,
  t,
}: ForecastReferenceLineProps) {
  if (!currentMonthId) {
    return null;
  }

  return (
    <ReferenceLine
      ifOverflow='extendDomain'
      label={{
        value: t('Forecast start'),
        position: 'top',
        fill: 'var(--chart-text)',
      }}
      stroke='var(--chart-reference-line)'
      strokeDasharray='5 5'
      strokeWidth={2}
      x={currentMonthId}
      yAxisId='sales'
      zIndex={DefaultZIndexes.line + 1}
    />
  );
}

function ForecastTooltip({ t }: ForecastTooltipProps) {
  return (
    <ChartTooltip
      axisId={0}
      content={<ChartTooltipContent className='max-w-full bg-layout p-2' />}
      labelFormatter={(label) => `${t('Month')}: ${label}`}
      wrapperStyle={{
        maxWidth: 'min(20rem, calc(100vw - 2rem))',
        pointerEvents: 'none',
        width: 'max-content',
      }}
    />
  );
}

function ForecastSeries() {
  return linesConfig.map((config) => {
    const key = String(config.dataKey);

    if (config.chartType === 'area') {
      const { chartType, dataKey, yAxisId, ...seriesProps } = config;
      return (
        <Area
          dataKey={dataKey}
          key={key}
          type='monotone'
          yAxisId={yAxisId}
          {...seriesProps}
        />
      );
    }

    const { chartType, dataKey, yAxisId, ...seriesProps } = config;
    return (
      <Line
        dataKey={dataKey}
        key={key}
        type='monotone'
        yAxisId={yAxisId}
        {...seriesProps}
      />
    );
  });
}

export function ForecastChart({
  data,
  className,
  isNovelty = false,
}: ForecastChartProps) {
  const { t } = useTranslation('forecast');
  const chartConfig = useMemo(() => buildChartConfig(t), [t]);
  const { chartData, currentMonthId, chartKey } = useForecastChart(
    data,
    isNovelty,
  );

  return (
    <ChartContainer className={cn(className)} config={chartConfig}>
      {() => (
        <ComposedChart<ForecastDataPoint>
          accessibilityLayer={true}
          data={chartData}
          key={chartKey}
          margin={{
            top: 20,
            right: 80,
            bottom: 60,
            left: 80,
          }}
        >
          <defs>
            <linearGradient id='stockGradient' x1='0' x2='0' y1='0' y2='1'>
              <stop
                offset='5%'
                stopColor='var(--chart-stock)'
                stopOpacity={0.4}
              />
              <stop
                offset='95%'
                stopColor='var(--chart-stock)'
                stopOpacity={0.1}
              />
            </linearGradient>
          </defs>
          <CartesianGrid
            stroke='var(--chart-grid)'
            strokeDasharray='3 3'
            xAxisId={0}
            yAxisId='sales'
            zIndex={DefaultZIndexes.grid}
          />
          <ForecastAxes t={t} />
          <ForecastCurrentMonthReference
            currentMonthId={currentMonthId}
            t={t}
          />
          <ForecastTooltip t={t} />
          <ForecastSeries />
        </ComposedChart>
      )}
    </ChartContainer>
  );
}
